import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { supabase } from '../lib/supabase'
import { generateDailyTasksFromTemplates, todayISO } from '../lib/tasks'
import { useAuth } from './AuthContext'

const CareContext = createContext(null)

const ACTIVE_KEY = 'mapa.activeRecipientId'

export function CareProvider({ children }) {
  const { user, loading: authLoading } = useAuth()
  const [memberships, setMemberships] = useState([]) // [{ recipient, role, membershipId }]
  const [activeId, setActiveId] = useState(
    () => localStorage.getItem(ACTIVE_KEY) || null
  )
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    // Auth restores the session asynchronously. Until it settles, `user` is
    // null — reporting "loaded, no recipients" here makes CareGate bounce a
    // returning user into onboarding, and it never navigates back.
    if (authLoading) {
      setLoading(true)
      return
    }
    if (!user) {
      setMemberships([])
      setLoading(false)
      return
    }
    setLoading(true)

    // If this user arrived via an invite link before authenticating, accept it now.
    const pendingInvite = localStorage.getItem('mapa.pendingInvite')
    if (pendingInvite) {
      localStorage.removeItem('mapa.pendingInvite')
      const { error } = await supabase.rpc('accept_invite', { p_token: pendingInvite })
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('Could not auto-accept invite:', error.message)
      }
    }
    // One query: my active memberships + the recipient they point to.
    const { data, error } = await supabase
      .from('care_team_members')
      .select('id, role, relationship, care_recipient:care_recipients(*)')
      .eq('user_id', user.id)
      .eq('is_active', true)

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('Could not load care memberships:', error.message)
      setMemberships([])
      setLoading(false)
      return
    }

    const rows = (data || [])
      .filter((r) => r.care_recipient)
      .map((r) => ({
        membershipId: r.id,
        role: r.role,
        relationship: r.relationship,
        recipient: r.care_recipient,
      }))

    setMemberships(rows)
    setLoading(false)
  }, [user, authLoading])

  useEffect(() => {
    load()
  }, [load])

  // Keep a valid active recipient selected.
  useEffect(() => {
    if (loading) return
    if (memberships.length === 0) {
      setActiveId(null)
      return
    }
    const stillValid = memberships.some(
      (m) => m.recipient.id === activeId
    )
    if (!stillValid) {
      setActiveId(memberships[0].recipient.id)
    }
  }, [memberships, loading, activeId])

  useEffect(() => {
    if (activeId) localStorage.setItem(ACTIVE_KEY, activeId)
    else localStorage.removeItem(ACTIVE_KEY)
  }, [activeId])

  const active = memberships.find((m) => m.recipient.id === activeId) || null

  // Materialise today's recurring tasks once per recipient per day. Best
  // effort: a failure here must never block the app from rendering.
  const generatedFor = useRef('')
  useEffect(() => {
    if (!active?.recipient?.id) return
    const key = `${active.recipient.id}:${todayISO()}`
    if (generatedFor.current === key) return
    generatedFor.current = key
    generateDailyTasksFromTemplates(active.recipient.id)
  }, [active?.recipient?.id])

  const value = {
    loading,
    memberships,
    hasRecipients: memberships.length > 0,
    activeId,
    setActiveId,
    recipient: active?.recipient ?? null,
    role: active?.role ?? null,
    // Permission helpers mirroring the RLS policies in schema.sql
    isAdmin: active?.role === 'admin',
    isCaregiver: active?.role === 'caregiver',
    // Anyone who is admin on at least one recipient gets the all-clients
    // overview. Deliberately not scoped to the *active* recipient: a user can
    // be admin on one client and family on another, and should still reach it.
    isAdminAnywhere: memberships.some((m) => m.role === 'admin'),
    adminMemberships: memberships.filter((m) => m.role === 'admin'),
    canManageClinical:
      active?.role === 'admin' || active?.role === 'family_member',
    reload: load,
  }

  return <CareContext.Provider value={value}>{children}</CareContext.Provider>
}

export function useCare() {
  const ctx = useContext(CareContext)
  if (!ctx) throw new Error('useCare must be used within <CareProvider>')
  return ctx
}
